"use client";

import { ListSkeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/lib/supabase";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL ?? "";
const SUPABASE_KEY = process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? "";

type ToCreateRow = { email: string; fullName: string; department: string; position: string; team: string | null };
type ToUpdateRow = {
  email: string;
  userId: string;
  fullName: string;
  department: string;
  position: string;
  team: string | null;
  previousJobRole: string | null;
  previousTeam: string | null;
  wasInactive: boolean;
};
type ToFlagInactiveRow = { email: string; userId: string; fullName: string | null };
type ToIgnoreRow = {
  email: string;
  userId: string | null;
  fullName: string;
  department: string;
  position: string;
  reason: string;
};

type Diff = {
  fetchedCount: number;
  guard: { ok: boolean; lastSuccessCount: number | null };
  toCreate: ToCreateRow[];
  toUpdate: ToUpdateRow[];
  toFlagInactive: ToFlagInactiveRow[];
  toIgnore: ToIgnoreRow[];
};

async function callSync(token: string, payload: Record<string, unknown>) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/sync-project-central-users`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, apikey: SUPABASE_KEY, "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (!res.ok || json.error) throw new Error(json.error ?? "Request failed");
  return json;
}

export function SyncProjectCentralUsersDialog({
  open,
  onOpenChange,
  onApplied,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplied: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [diff, setDiff] = useState<Diff | null>(null);
  const [checkedCreate, setCheckedCreate] = useState<Set<string>>(new Set());
  const [checkedUpdate, setCheckedUpdate] = useState<Set<string>>(new Set());
  const [checkedFlag, setCheckedFlag] = useState<Set<string>>(new Set());

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setDiff(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token ?? "";
      const result = (await callSync(token, { mode: "preview" })) as Diff;
      setDiff(result);
      setCheckedCreate(new Set(result.toCreate.map((r) => r.email)));
      setCheckedUpdate(new Set(result.toUpdate.map((r) => r.email)));
      setCheckedFlag(new Set(result.toFlagInactive.map((r) => r.email)));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load preview.");
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }, [onOpenChange]);

  useEffect(() => {
    if (open) void loadPreview();
    if (!open) setDiff(null);
  }, [open, loadPreview]);

  const toggle = (set: Set<string>, setSet: (s: Set<string>) => void, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSet(next);
  };

  const toggleAll = (set: Set<string>, setSet: (s: Set<string>) => void, keys: string[]) => {
    setSet(set.size === keys.length ? new Set() : new Set(keys));
  };

  const totalChecked = checkedCreate.size + checkedUpdate.size + checkedFlag.size;

  const handleConfirm = useCallback(async () => {
    if (!diff) return;
    setApplying(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token ?? "";
      // Only tell the server which emails were checked — it re-fetches a
      // fresh diff and applies using its own computed values, so a stale or
      // tampered payload here can't write arbitrary data (ignored people are
      // always logged from that fresh diff too, not something to select).
      const selected = {
        toCreate: Array.from(checkedCreate),
        toUpdate: Array.from(checkedUpdate),
        toFlagInactive: Array.from(checkedFlag),
      };
      const result = await callSync(token, { mode: "apply", selected });
      toast.success(
        `Created ${result.createdCount}, updated ${result.updatedCount}, flagged ${result.flaggedCount} inactive.`
      );
      onApplied();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to apply sync.");
    } finally {
      setApplying(false);
    }
  }, [diff, checkedCreate, checkedUpdate, checkedFlag, onApplied, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] w-[98vw] max-w-[98vw] sm:max-w-[98vw] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Sync users from Project Central</DialogTitle>
          <DialogDescription>
            Review what this sync would do, uncheck anything you don&apos;t want applied, then confirm. To fix a
            wrong department mapping, edit department_team_mapping and re-open this dialog.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <ListSkeleton />
        ) : diff ? (
          <div className="flex-1 space-y-6 overflow-y-auto pr-1">
            {!diff.guard.ok && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                Fetched {diff.fetchedCount} people, but the last successful sync had{" "}
                {diff.guard.lastSuccessCount}. This looks like a partial fetch — review carefully before
                confirming, especially the &quot;Flag inactive&quot; table below.
              </div>
            )}

            <PreviewTable
              title="New accounts"
              rows={diff.toCreate}
              getKey={(r) => r.email}
              checked={checkedCreate}
              onToggle={(key) => toggle(checkedCreate, setCheckedCreate, key)}
              onToggleAll={(keys) => toggleAll(checkedCreate, setCheckedCreate, keys)}
              columns={[
                { header: "Name", render: (r) => <span className="font-medium text-foreground">{r.fullName}</span> },
                { header: "Email", render: (r) => r.email },
                { header: "Position", render: (r) => r.position },
                { header: "Department", render: (r) => r.department },
              ]}
            />

            <PreviewTable
              title="Updates"
              rows={diff.toUpdate}
              getKey={(r) => r.email}
              checked={checkedUpdate}
              onToggle={(key) => toggle(checkedUpdate, setCheckedUpdate, key)}
              onToggleAll={(keys) => toggleAll(checkedUpdate, setCheckedUpdate, keys)}
              columns={[
                { header: "Name", render: (r) => <span className="font-medium text-foreground">{r.fullName}</span> },
                { header: "Email", render: (r) => r.email },
                { header: "Position", render: (r) => r.position },
                { header: "Department", render: (r) => r.department },
                {
                  header: "Note",
                  render: (r) =>
                    r.wasInactive ? (
                      <Badge variant="outline" className="text-xs">
                        reactivating
                      </Badge>
                    ) : (
                      "—"
                    ),
                },
              ]}
            />

            <PreviewTable
              title="Flag inactive"
              rows={diff.toFlagInactive}
              getKey={(r) => r.email}
              checked={checkedFlag}
              onToggle={(key) => toggle(checkedFlag, setCheckedFlag, key)}
              onToggleAll={(keys) => toggleAll(checkedFlag, setCheckedFlag, keys)}
              columns={[
                { header: "Name", render: (r) => r.fullName ?? "—" },
                { header: "Email", render: (r) => r.email },
              ]}
            />

            <PreviewTable
              title="Ignored"
              rows={diff.toIgnore}
              getKey={(r) => r.email}
              columns={[
                { header: "Name", render: (r) => <span className="font-medium text-foreground">{r.fullName}</span> },
                { header: "Email", render: (r) => r.email },
                { header: "Position", render: (r) => r.position },
                { header: "Department", render: (r) => r.department },
                { header: "Reason", render: (r) => r.reason },
              ]}
            />
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={loading || applying || !diff || totalChecked === 0}
          >
            {applying ? "Applying…" : `Confirm (${totalChecked})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewTable<T>({
  title,
  rows,
  getKey,
  checked,
  onToggle,
  onToggleAll,
  columns,
}: {
  title: string;
  rows: T[];
  getKey: (row: T) => string;
  /** Omit all three to render a read-only table with no checkboxes (e.g. "Ignored" — nothing to apply). */
  checked?: Set<string>;
  onToggle?: (key: string) => void;
  onToggleAll?: (keys: string[]) => void;
  columns: { header: string; render: (row: T) => ReactNode }[];
}) {
  const keys = rows.map(getKey);
  const selectable = checked !== undefined && onToggle !== undefined && onToggleAll !== undefined;
  const allChecked = selectable && keys.length > 0 && keys.every((k) => checked.has(k));

  return (
    <div>
      <h3 className="mb-2 text-sm font-medium text-foreground">
        {title} ({rows.length})
      </h3>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nothing to do.</p>
      ) : (
        <div className="rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                {selectable && (
                  <TableHead className="w-8">
                    <Checkbox checked={allChecked} onCheckedChange={() => onToggleAll(keys)} />
                  </TableHead>
                )}
                {columns.map((col) => (
                  <TableHead key={col.header}>{col.header}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const key = getKey(row);
                return (
                  <TableRow key={key}>
                    {selectable && (
                      <TableCell>
                        <Checkbox checked={checked.has(key)} onCheckedChange={() => onToggle(key)} />
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell key={col.header} className="text-muted-foreground">
                        {col.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
